import { isAbsentOrPossiblePhone, isPossiblePhone, toE164Phone } from '@arrival/shared/validation/phone'

export type CompleteBookingGuest = {
  email: string
  name: string
  phone?: string
}

export const BOOKING_GUEST_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type BookingGuestIdentitySource = {
  name?: string | null
  email?: string | null
  phone?: string | null
}

export type BookingGuestFieldGaps = {
  needsName: boolean
  needsEmail: boolean
  /** True only when a non-empty phone is present but invalid. */
  needsPhone: boolean
}

export function isValidBookingGuestEmail(email: string): boolean {
  return BOOKING_GUEST_EMAIL_PATTERN.test(email.trim())
}

export function isValidBookingGuestName(name: string): boolean {
  return name.trim().length >= 2
}

export function isCompleteBookingGuestFields(
  fields: BookingGuestIdentitySource | null | undefined,
): fields is CompleteBookingGuest {
  if (!fields) {
    return false
  }
  const email = fields.email?.trim() ?? ''
  const name = fields.name?.trim() ?? ''
  const phone = fields.phone?.trim() ?? ''
  return isValidBookingGuestEmail(email) && isValidBookingGuestName(name) && isAbsentOrPossiblePhone(phone)
}

export function toCompleteBookingGuest(
  fields: BookingGuestIdentitySource | null | undefined,
): CompleteBookingGuest | null {
  if (!isCompleteBookingGuestFields(fields)) {
    return null
  }
  const phone = fields.phone?.trim() ?? ''
  const parsedPhone = phone ? toE164Phone(phone) : null
  return {
    email: fields.email.trim().toLowerCase(),
    name: fields.name.trim(),
    ...(parsedPhone ? { phone: parsedPhone } : {}),
  }
}

/** Missing required fields from stored sources (form inputs not included). */
export function bookingGuestFieldGaps(input: {
  guest?: BookingGuestIdentitySource | null
  clerk?: { name?: string | null; email?: string | null } | null
  pending?: BookingGuestIdentitySource | null
}): BookingGuestFieldGaps {
  const name = input.guest?.name ?? input.clerk?.name ?? input.pending?.name ?? ''
  const email = input.guest?.email ?? input.clerk?.email ?? input.pending?.email ?? ''
  const phone = input.guest?.phone ?? input.pending?.phone ?? ''
  const phoneTrimmed = phone.trim()
  return {
    needsName: !isValidBookingGuestName(name),
    needsEmail: !isValidBookingGuestEmail(email),
    needsPhone: phoneTrimmed.length > 0 && !isPossiblePhone(phoneTrimmed),
  }
}

/**
 * Convex guest row → complete pending → Clerk name/email + pending phone.
 * Null when email or name is missing/invalid, or phone is present but invalid.
 */
export function resolveBookingGuestIdentity(input: {
  guest?: BookingGuestIdentitySource | null
  clerk?: { name?: string | null; email?: string | null } | null
  pending?: BookingGuestIdentitySource | null
}): CompleteBookingGuest | null {
  const fromGuest = toCompleteBookingGuest(input.guest)
  if (fromGuest) {
    return fromGuest
  }

  const fromPending = toCompleteBookingGuest(input.pending)
  if (fromPending) {
    return fromPending
  }

  return toCompleteBookingGuest({
    name: input.clerk?.name ?? input.pending?.name,
    email: input.clerk?.email ?? input.pending?.email,
    phone: input.pending?.phone,
  })
}

function normalizeGuestFieldSnapshot(fields: { name: string; email: string; phone: string }) {
  return {
    name: fields.name.trim(),
    email: fields.email.trim().toLowerCase(),
    phone: toE164Phone(fields.phone) ?? fields.phone.trim(),
  }
}

/** True when the booking form no longer matches the signed-in / prefilled default. */
export function bookingGuestFieldsDifferFromDefault(
  current: { name: string; email: string; phone: string },
  baseline: { name: string; email: string; phone: string },
): boolean {
  const next = normalizeGuestFieldSnapshot(current)
  const from = normalizeGuestFieldSnapshot(baseline)
  return next.name !== from.name || next.email !== from.email || next.phone !== from.phone
}

/** Safe to overwrite form with a newer default (empty, or still equal to last auto-fill). */
export function shouldReplaceBookingGuestPrefill(args: {
  current: { name: string; email: string; phone: string }
  lastApplied: { name: string; email: string; phone: string } | null
}): boolean {
  if (!args.lastApplied) {
    return !args.current.name.trim() && !args.current.email.trim() && !args.current.phone.trim()
  }
  return !bookingGuestFieldsDifferFromDefault(args.current, args.lastApplied)
}
